import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import Swal from 'sweetalert2';
import {
  InterviewService,
  CreateInterviewPayload,
  B2bInterviewVideoRequest,
  JobPostDetailsResponse,
  InterviewViewResponse,
  InterviewViewQuestion,
  CreatePublicInterviewPayload,
  UpdateInterviewPayload,
} from './interview.service';
import { LocalizationService } from 'src/app/services/localization/localization.service';

interface InterviewQuestion {
  question: string; // → questionText
  clarification: string; // → explainText
  answer: string; // → expectedAnswer
  duration: number; // → duration (minutes, entered by the user)
}

@Component({
  selector: 'app-interview-setup',
  standalone: false,
  templateUrl: './interview-setup.component.html',
  styleUrls: ['./interview-setup.component.css'],
})
export class InterviewSetupComponent implements OnInit, OnDestroy {
  /** Fixed product key & price as defined in products.json */
  readonly INTERVIEW_PRODUCT_KEY = 'b2b-interview';
  readonly INTERVIEW_PRICE = 200;

  questions: InterviewQuestion[] = [
    { question: '', clarification: '', answer: '', duration: null as any },
  ];

  positionTitle = '';
  positionJobLevel = '';
  positionDepartment = '';
  positionJobType = '';
  jobPostTitle = '';
  description = '';
  interviewTitle = '';
  positionJobLevelDropdownOpen = false;

  readonly positionJobLevelOptions = [
    { label: 'Entry level', value: 'ENTRY_LEVEL' },
    { label: 'Experienced', value: 'EXPERIENCED' },
    { label: 'Manager', value: 'MANAGER' },
    { label: 'Senior management', value: 'SENIOR_MANAGEMENT' },
    { label: 'C-Level', value: 'C_LEVEL' },
    { label: 'Student', value: 'STUDENT' },
  ];

  selectedJobPostDetails: JobPostDetailsResponse | null = null;

  selectedJobPostId: string | number = '';
  totalInterviewTime = '';

  /** Language — backend accepts: EN | AR | FR */
  selectedLanguage: 'EN' | 'AR' | 'FR' = 'EN';

  /** Previous app language — restored on component destroy */
  private previousLang: string = '';

  /** Expiration date for the interview */
  expirationDate: string = '';

  /** Today's date (min for date picker) */
  todayDate: string = new Date().toISOString().split('T')[0];

  /** Max additional answers */
  selectedAdditionalAnswers: number | null = null;
  additionalAnswerDuration: number | null = null;

  readonly commentTimeOptions = [
    { value: 1, minimumQuestions: 3, label: 'Minimum 3 questions' },
    { value: 2, minimumQuestions: 4, label: 'Minimum 4 questions' },
    { value: 3, minimumQuestions: 8, label: 'Minimum 8 questions' },
    { value: 5, minimumQuestions: 15, label: 'Minimum 15 questions' },
  ];

  /** Number of interview link uses */
  selectedLinkCount = null as any;

  /** Payment modal state */
  showPaymentModal = false;

  /** Loading state while creating interview */
  isCreating = false;

  /** ID of created interview — used as serviceSystemId for payment */
  private createdInterviewId: string | number = '';

  isJobPostDropdownOpen = false;
  isCopied = false;
  /** Interview type from query params or API — used to show/hide Copy Link */
  interviewType = '';

  // ── View Mode ─────────────────────────────────────────────────────────────
  /** When true, form is read-only and shows fetched interview data */
  viewMode = false;
  editMode = false;
  viewData: InterviewViewResponse | null = null;
  viewLoading = false;

  get selectedJobPostPosition(): string {
    return (
      this.selectedJobPostDetails?.positionTitle ??
      this.fallbackPosition ??
      this.selectedJobPostName ??
      'Not specified'
    );
  }

  get standaloneInterviewTitle(): string {
    return this.interviewTitle?.trim() || 'Interview Title';
  }

  get selectedPositionJobLevelLabel(): string {
    const selected = this.positionJobLevelOptions.find(
      (opt) => opt.value === this.positionJobLevel,
    );
    return selected ? selected.label : 'Choose position level';
  }

  get selectedJobPostName(): string {
    return (
      this.selectedJobPostDetails?.jobPostTitle ??
      this.fallbackJobName ??
      'Not specified'
    );
  }

  fallbackJobName: string = '';
  fallbackPosition: string = '';

  get isStandalone(): boolean {
    return !this.selectedJobPostId && !this.viewMode && !this.editMode;
  }

  /** In view mode: true if the interview was linked to a job post */
  get viewModeHasJobPost(): boolean {
    return this.viewMode && !!this.viewData?.jobPostId;
  }

  /** In view mode: true if the interview is standalone (no job post) */
  get viewModeIsStandalone(): boolean {
    return this.viewMode && !this.viewData?.jobPostId;
  }

  get showInterviewLinkInView(): boolean {
    return (
      this.viewMode &&
      !!this.viewData?.interviewLink &&
      !this.viewData?.jobPostId
    );
  }

  constructor(
    private interviewService: InterviewService,
    private router: Router,
    private route: ActivatedRoute,
    private localizationService: LocalizationService,
  ) {}

  ngOnInit(): void {
    // Save current app language to restore later
    this.previousLang = localStorage.getItem('userLang') || 'en';
    // Set initial selectedLanguage based on current app language
    this.selectedLanguage = this.previousLang === 'ar' ? 'AR' : 'EN';

    this.route.queryParams.subscribe((params) => {
      if (params['id']) {
        this.viewMode = params['mode'] === 'view';
        this.editMode = params['mode'] === 'edit';
        this.interviewTitle = params['title'] ?? '';
        if (params['type']) this.interviewType = params['type'];
        this.loadInterviewById(params['id']);
      } else {
        this.viewMode = false;
        this.editMode = false;
        this.viewData = null;
        this.selectedJobPostId = '';
        this.selectedJobPostDetails = null;
        this.fallbackJobName = '';
        this.fallbackPosition = '';
        this.interviewTitle = '';
        this.positionTitle = '';
        this.positionJobLevel = '';
        this.positionDepartment = '';
        this.positionJobType = '';
        this.jobPostTitle = '';
        this.description = '';
        this.selectedAdditionalAnswers = null;
        this.additionalAnswerDuration = null;
        this.totalInterviewTime = '';
        this.selectedLinkCount = null;
        this.expirationDate = '';

        if (params['jobId']) {
          this.selectedJobPostId = params['jobId'];
          if (!isNaN(Number(params['jobId']))) {
            this.selectedJobPostId = Number(params['jobId']);
          }
          this.loadSelectedJobPostDetails(this.selectedJobPostId);
        }
        if (params['jobName']) this.fallbackJobName = params['jobName'];
        if (params['position']) this.fallbackPosition = params['position'];
      }
    });
  }

  get userLang(): string {
    return this.localizationService.getUserLang() || 'en';
  }

  ngOnDestroy(): void {
    // Restore previous app language when leaving
    if (this.previousLang) {
      this.localizationService.setUserLang(this.previousLang);
    }
  }

  @HostListener('document:click')
  closeDropdowns(): void {
    this.positionJobLevelDropdownOpen = false;
  }

  /** Fetch full interview details for view mode */
  private loadInterviewById(id: string): void {
    this.viewLoading = true;
    this.interviewService.getInterviewById(id).subscribe({
      next: (res) => {
        this.viewData = res;
        this.viewLoading = false;
        console.log(this.viewData);

        // ── Populate form fields from API data ──────────────────────────────
        // Language
        this.selectedLanguage = res.language ?? 'EN';

        // Interview type — update from API if available (overrides query param)
        if (res.type) this.interviewType = res.type;

        // Interview title
        this.interviewTitle =
          res.interviewTitle?.trim() || this.interviewTitle || '';

        // Job post info
        this.selectedJobPostId = res.jobPostId ?? '';
        this.fallbackJobName = res.jobPostTitle ?? '';
        this.fallbackPosition = res.positionTitle ?? '';

        // Standalone fields (when no jobPostId)
        this.positionTitle = res.positionTitle ?? '';
        this.positionJobLevel = res.positionJobLevel ?? '';
        this.positionDepartment = res.positionDepartment ?? '';
        this.positionJobType = res.positionJobType ?? '';
        this.jobPostTitle = res.jobPostTitle ?? '';
        this.description = res.description ?? '';

        // Expiration date: "2027-02-06T23:59:59" → "2027-02-06"
        if (res.expirationDate) {
          this.expirationDate = res.expirationDate.split('T')[0];
        }

        // Total session duration
        this.totalInterviewTime = res.totalSessionDuration
          ? String(res.totalSessionDuration)
          : '';

        // Allowed links count
        this.selectedLinkCount = res.allowedLinksCount ?? null;

        // Max additional answers
        this.selectedAdditionalAnswers = res.maxAdditionalAnswers ?? null;

        // Questions: use actual questions array from API if available,
        // otherwise fallback to merging keywords + instructions.
        // NOTE: backend stores duration in SECONDS, form uses MINUTES,
        // so we divide by 60 when loading data back into the form.
        if (res.questions && res.questions.length > 0) {
          this.questions = res.questions
            .slice()
            .sort(
              (a: InterviewViewQuestion, b: InterviewViewQuestion) =>
                a.videoOrder - b.videoOrder,
            )
            .map((q: InterviewViewQuestion) => ({
              question: q.questionText ?? '',
              clarification: q.explainText ?? '',
              answer: q.expectedAnswer ?? '',
              duration:
                q.duration != null
                  ? Math.round((q.duration / 60) * 100) / 100
                  : (null as any),
            }));
        } else {
          const keywords = res.keywords ?? [];
          const instructions = res.instructions ?? [];
          const maxLen = Math.max(keywords.length, instructions.length, 1);
          this.questions = Array.from({ length: maxLen }, (_, i) => ({
            question: keywords[i] ?? '',
            clarification: instructions[i] ?? '',
            answer: '',
            duration: null as any,
          }));
        }
      },
      error: (err) => {
        console.error('Failed to load interview details:', err);
        this.viewLoading = false;
      },
    });
  }

  /** Switch UI language + store for backend payload */
  onLanguageChange(lang: 'EN' | 'AR' | 'FR'): void {
    this.selectedLanguage = lang;
    this.localizationService.setUserLang(lang.toLowerCase());
  }

  get isReadOnlyField(): boolean {
    return this.viewMode || (this.editMode && !!this.selectedJobPostId);
  }

  get canEditJobPostFields(): boolean {
    return this.editMode && !this.selectedJobPostId;
  }

  private loadSelectedJobPostDetails(jobPostId: string | number): void {
    if (!jobPostId) return;

    this.interviewService.getJobPostById(jobPostId).subscribe({
      next: (res) => {
        this.selectedJobPostDetails = res;
      },
      error: (err) => {
        console.error('Failed to load selected job post details:', err);
        this.selectedJobPostDetails = null;
      },
    });
  }

  addQuestion(): void {
    this.questions.push({
      question: '',
      clarification: '',
      answer: '',
      duration: null as any,
    });
  }

  removeQuestion(index: number): void {
    if (this.questions.length > 1) {
      this.questions.splice(index, 1);
    }
  }

  trackByIndex(index: number): number {
    return index;
  }

  /**
   * Opens the payment/confirmation modal.
   * - In view mode (unpaid interview): skip validation, just open the modal —
   *   the data is already saved, we only need payment confirmation.
   * - In edit mode / create mode: run full validation first.
   */
  openPaymentModal(): void {
    // View mode: interview already exists, only payment is missing.
    if (this.viewMode) {
      this.showPaymentModal = true;
      return;
    }

    const isAr =
      this.selectedLanguage === 'AR' ||
      localStorage.getItem('userLang') === 'ar';

    if (this.isStandalone) {
      if (!this.positionTitle || this.positionTitle.trim() === '') {
        Swal.fire({
          icon: 'warning',
          title: isAr ? 'تنبيه' : 'Warning',
          text: isAr
            ? 'يرجى إدخال المسمى الوظيفي.'
            : 'Please enter the position title.',
          confirmButtonText: isAr ? 'حسناً' : 'OK',
          confirmButtonColor: '#e8392a',
        });
        return;
      }
      if (!this.positionJobLevel || this.positionJobLevel.trim() === '') {
        Swal.fire({
          icon: 'warning',
          title: isAr ? 'تنبيه' : 'Warning',
          text: isAr
            ? 'يرجى إدخال المستوى الوظيفي.'
            : 'Please enter the job position.',
          confirmButtonText: isAr ? 'حسناً' : 'OK',
          confirmButtonColor: '#e8392a',
        });
        return;
      }
    }

    if (!this.interviewTitle || this.interviewTitle.trim() === '') {
      Swal.fire({
        icon: 'warning',
        title: isAr ? 'تنبيه' : 'Warning',
        text: isAr
          ? 'يرجى إدخال عنوان المقابلة.'
          : 'Please enter the interview title.',
        confirmButtonText: isAr ? 'حسناً' : 'OK',
        confirmButtonColor: '#e8392a',
      });
      return;
    }

    const hasValidQuestion = this.questions.some(
      (q) => q.question && q.question.trim() !== '',
    );
    if (!hasValidQuestion) {
      Swal.fire({
        icon: 'warning',
        title: isAr ? 'تنبيه' : 'Warning',
        text: isAr
          ? 'يرجى إدخال سؤال واحد على الأقل.'
          : 'Please enter at least one question.',
        confirmButtonText: isAr ? 'حسناً' : 'OK',
        confirmButtonColor: '#e8392a',
      });
      return;
    }

    const invalidDuration = this.questions.some(
      (q) =>
        q.question &&
        q.question.trim() !== '' &&
        (!q.duration || q.duration <= 0),
    );
    if (invalidDuration) {
      Swal.fire({
        icon: 'warning',
        title: isAr ? 'تنبيه' : 'Warning',
        text: isAr
          ? 'يرجى تحديد مدة كل سؤال.'
          : 'Please specify the duration for each question.',
        confirmButtonText: isAr ? 'حسناً' : 'OK',
        confirmButtonColor: '#e8392a',
      });
      return;
    }

    if (!this.totalInterviewTime || Number(this.totalInterviewTime) <= 0) {
      Swal.fire({
        icon: 'warning',
        title: isAr ? 'تنبيه' : 'Warning',
        text: isAr
          ? 'يرجى إدخال الوقت الإجمالي للمقابلة.'
          : 'Please enter the total interview time.',
        confirmButtonText: isAr ? 'حسناً' : 'OK',
        confirmButtonColor: '#e8392a',
      });
      return;
    }

    if (!this.selectedLinkCount || this.selectedLinkCount <= 0) {
      Swal.fire({
        icon: 'warning',
        title: isAr ? 'تنبيه' : 'Warning',
        text: isAr
          ? 'يرجى تحديد عدد مرات الاستخدام المسموحة للرابط.'
          : 'Please specify a valid number of uses for the link.',
        confirmButtonText: isAr ? 'حسناً' : 'OK',
        confirmButtonColor: '#e8392a',
      });
      return;
    }

    if (!this.expirationDate) {
      Swal.fire({
        icon: 'warning',
        title: isAr ? 'تنبيه' : 'Warning',
        text: isAr
          ? 'يرجى تحديد تاريخ انتهاء الصلاحية.'
          : 'Please specify the expiration date.',
        confirmButtonText: isAr ? 'حسناً' : 'OK',
        confirmButtonColor: '#e8392a',
      });
      return;
    }

    if (this.editMode) {
      Swal.fire({
        icon: 'question',
        title: isAr ? 'تأكيد التعديل' : 'Confirm update',
        text: isAr
          ? 'هل أنت متأكد من حفظ التعديلات؟'
          : 'Are you sure you want to save these changes?',
        showCancelButton: true,
        confirmButtonText: isAr ? 'نعم، حفظ' : 'Yes, save',
        cancelButtonText: isAr ? 'إلغاء' : 'Cancel',
        confirmButtonColor: '#0a376e',
        cancelButtonColor: '#6c757d',
      }).then((result) => {
        if (result.isConfirmed) {
          this.confirmUpdate();
        }
      });
      return;
    }

    this.showPaymentModal = true;
  }

  togglePositionJobLevelDropdown(event: Event): void {
    event.stopPropagation();
    this.positionJobLevelDropdownOpen = !this.positionJobLevelDropdownOpen;
  }

  selectPositionJobLevel(value: string, event: Event): void {
    event.stopPropagation();
    this.positionJobLevel = value;
    this.positionJobLevelDropdownOpen = false;
  }

  closePaymentModal(): void {
    if (!this.isCreating) {
      this.showPaymentModal = false;
    }
  }

  isCommentTimeEnabled(optionValue: number): boolean {
    const option = this.commentTimeOptions.find(
      (item) => item.value === optionValue,
    );
    return !!option && this.questions.length >= option.minimumQuestions;
  }

  /**
   * Build videos array from questions.
   * IMPORTANT: `q.duration` is entered by the user in MINUTES in the form,
   * but the backend expects `duration` in SECONDS — so we multiply by 60
   * right here, in the single place that builds the payload for
   * create/update, so every caller (standalone create, linked create,
   * update) gets the conversion automatically.
   */
  private buildVideos(): B2bInterviewVideoRequest[] {
    return this.questions
      .filter((q) => q.question.trim())
      .map((q, index) => ({
        videoOrder: index + 1,
        duration: q.duration ? Math.round(q.duration * 60) : 0,
        questionText: q.question.trim(),
        explainText: q.clarification.trim(),
        expectedAnswer: q.answer.trim() || undefined,
      }));
  }

  private buildUpdatePayload(): UpdateInterviewPayload {
    const additionalAnswers =
      this.selectedAdditionalAnswers === null ||
      this.selectedAdditionalAnswers === undefined
        ? null
        : Number(this.selectedAdditionalAnswers);
    const additionalAnswerDuration =
      this.additionalAnswerDuration === null ||
      this.additionalAnswerDuration === undefined
        ? null
        : Number(this.additionalAnswerDuration);
    const totalSessionDuration = Number(this.totalInterviewTime) || undefined;

    const basePayload: UpdateInterviewPayload = {
      interviewTitle: this.interviewTitle.trim(),
      expirationDate: this.expirationDate
        ? `${this.expirationDate}T23:59:59`
        : undefined,
      instructions: this.questions
        .filter((q) => q.clarification.trim())
        .map((q) => q.clarification.trim()),
      keywords: this.questions
        .filter((q) => q.question.trim())
        .map((q) => q.question.trim()),
      language: this.selectedLanguage,
      allowedLinksCount: this.selectedLinkCount,
      totalSessionDuration,
      additionalAnswers,
      additionalAnswerDuration,
      videos: this.buildVideos(),
    };

    if (!this.selectedJobPostId) {
      return {
        ...basePayload,
        positionTitle: this.positionTitle.trim(),
        positionJobLevel: this.positionJobLevel.trim(),
      };
    }

    return {
      ...basePayload,
      jobPostId: this.selectedJobPostId,
    };
  }

  private confirmUpdate(): void {
    if (!this.viewData?.id) return;

    const payload = this.buildUpdatePayload();
    this.isCreating = true;

    this.interviewService.updateInterview(this.viewData.id, payload).subscribe({
      next: () => {
        this.isCreating = false;
        this.router.navigate(['/company-admin/virtual-interviews']);
      },
      error: (err) => {
        console.error('Failed to update interview:', err);
        this.isCreating = false;
      },
    });
  }

  copyInterviewLink(): void {
    const link = this.viewData?.interviewLink?.trim();
    if (!link) return;

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(link).then(() => {
        this.isCopied = true;
        setTimeout(() => {
          this.isCopied = false;
        }, 2000);
      }).catch(() => {
        console.error('Failed to copy interview link.');
      });
    }
  }

  switchToEditMode(): void {
    this.viewMode = false;
    this.editMode = true;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { mode: 'edit' },
      queryParamsHandling: 'merge'
    });
  }

  /**
   * Step 1: Create the interview — Step 2: Navigate to payment.
   * In view mode, the interview already exists (just unpaid) — skip creation
   * and go straight to the payment page using the existing interview id.
   */
  confirmPayment(): void {
    if (this.isCreating) return;

    // View mode: interview already exists — only payment is missing.
    if (this.viewMode && this.viewData?.id) {
      this.showPaymentModal = false;
      this.router.navigate(['/payment'], {
        state: {
          productKey: this.INTERVIEW_PRODUCT_KEY,
          serviceSystemId: String(this.viewData.id),
          amount: this.INTERVIEW_PRICE,
        },
        queryParams: {
          productKey: this.INTERVIEW_PRODUCT_KEY,
          serviceSystemId: String(this.viewData.id),
          amount: this.INTERVIEW_PRICE,
        },
      });
      return;
    }

    // Format date: "2026-12-31" → "2026-12-31T23:59:59"
    const expiry = this.expirationDate
      ? `${this.expirationDate}T23:59:59`
      : '2026-12-31T23:59:59';

    const additionalAnswers =
      this.selectedAdditionalAnswers === null ||
      this.selectedAdditionalAnswers === undefined
        ? null
        : Number(this.selectedAdditionalAnswers);
    const additionalAnswerDuration =
      this.additionalAnswerDuration === null ||
      this.additionalAnswerDuration === undefined
        ? null
        : Number(this.additionalAnswerDuration);
    const totalSessionDuration = Number(this.totalInterviewTime) || undefined;

    this.isCreating = true;
    const request$ = this.isStandalone
      ? this.interviewService.createPublicInterview({
          positionTitle: this.positionTitle.trim(),
          positionJobLevel: this.positionJobLevel.trim(),
          positionDepartment: this.positionDepartment.trim() || undefined,
          positionJobType: this.positionJobType.trim() || undefined,
          jobPostTitle: this.jobPostTitle.trim() || undefined,
          description: this.description.trim() || undefined,
          interviewTitle: this.interviewTitle.trim(),
          expirationDate: expiry,
          instructions: this.questions
            .filter((q) => q.clarification.trim())
            .map((q) => q.clarification.trim()),
          keywords: this.questions
            .filter((q) => q.question.trim())
            .map((q) => q.question.trim()),
          language: this.selectedLanguage,
          allowedLinksCount: this.selectedLinkCount,
          totalSessionDuration,
          additionalAnswers,
          additionalAnswerDuration,
          videos: this.buildVideos(),
        })
      : this.interviewService.createInterview({
          interviewTitle: this.interviewTitle.trim(),
          jobPostId: this.selectedJobPostId,
          expirationDate: expiry,
          instructions: this.questions
            .filter((q) => q.clarification.trim())
            .map((q) => q.clarification.trim()),
          keywords: this.questions
            .filter((q) => q.question.trim())
            .map((q) => q.question.trim()),
          language: this.selectedLanguage,
          maxAdditionalAnswers: additionalAnswers,
          additionalAnswers,
          additionalAnswerDuration,
          allowedLinksCount: this.selectedLinkCount,
          totalSessionDuration,
          videos: this.buildVideos(),
        });

    request$.subscribe({
      next: (res) => {
        this.createdInterviewId = res.id ?? '';
        this.isCreating = false;

        this.showPaymentModal = false;
        this.router.navigate(['/payment'], {
          state: {
            productKey: this.INTERVIEW_PRODUCT_KEY,
            serviceSystemId: String(this.createdInterviewId),
            amount: this.INTERVIEW_PRICE,
          },
          queryParams: {
            productKey: this.INTERVIEW_PRODUCT_KEY,
            serviceSystemId: String(this.createdInterviewId),
            amount: this.INTERVIEW_PRICE,
          },
        });
      },
      error: (err) => {
        console.error('Failed to create interview:', err);
        this.isCreating = false;
      },
    });
  }

  /** Format ISO date string to readable date */
  formatDate(dateStr: string): string {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  }

  /** Navigate back to virtual interviews list */
  goBack(): void {
    this.router.navigate(['/company-admin/virtual-interviews']);
  }
}
